import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { App, Button, Checkbox, Input, Popover, Select, Space, Table, Tooltip, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { useState, type JSX, type MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';
import { apiErrorMessage } from '../../api/client';
import { categoriesApi, operationsApi, reportsApi } from '../../api/endpoints';
import { invalidateMoneyData, queryKeys } from '../../api/queryKeys';
import type { ExpenseCategory, PendingColumn, StoreDayGridDay } from '../../api/types';
import { AmountInput } from '../../components/AmountInput';
import { ErrorBlock, LoadingBlock } from '../../components/PageStates';
import { balanceColor, hexTint, UNUSUAL_NEGATIVE } from '../../lib/balanceColors';
import { formatKopeks, kopeksToRubString, rubToKopeks } from '../../lib/money';
import { useStickyTable } from '../../context/StickyTableContext';
import { jadvalReturnTo } from '../../lib/returnTo';
import { DayDetailDrawer } from './DayDetailDrawer';
import { PendingCellPopover } from './PendingCellPopover';
import { PendingShipmentsCell, pendingShipmentsLabel } from './PendingShipmentsCell';

/** DOM id of today's row, so we can scroll it into view on mount. */
const TODAY_ROW_ID = 'jadval-today-row';

/** Right-aligned money, «—» for zero, tabular figures for column alignment. */
function money(n: number): JSX.Element {
  return n ? (
    <span style={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
      {formatKopeks(n)}
    </span>
  ) : (
    <Typography.Text type="secondary">—</Typography.Text>
  );
}

/**
 * Approved value on top, the «ожидает» line under it — two separate tap targets
 * in one cell: the top one edits approved money as before, the bottom one opens
 * the approval popover. Collapses to just the top line when nothing is pending.
 */
function CellStack({
  main,
  pending,
}: {
  main: JSX.Element;
  pending: JSX.Element | null;
}): JSX.Element {
  if (!pending) return main;
  return <div style={{ display: 'grid', justifyItems: 'end', gap: 1 }}>{main}{pending}</div>;
}

/**
 * Editable money cell: tap to type the day's value; empty clears it. Shared by
 * the Oylik (salary) and Наличные/Терминал (cash/terminal) columns — each passes
 * its own `save` (the matching upsert endpoint). `disabled` greys it out with a
 * tooltip (salary needs its expense category configured first). */
function EditableMoneyCell({
  amount,
  save: saveFn,
  disabled = false,
  disabledHint,
}: {
  amount: number;
  save: (kopeks: number) => Promise<unknown>;
  disabled?: boolean;
  disabledHint?: string;
}): JSX.Element {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState('');

  const mutation = useMutation({
    mutationFn: (kopeks: number) => saveFn(kopeks),
    onSuccess: () => {
      invalidateMoneyData(queryClient);
      setOpen(false);
    },
    onError: (error: Error) => message.error(apiErrorMessage(error) ?? t('common.requestError')),
  });

  const startEdit = (): void => {
    setValue(amount ? kopeksToRubString(amount) : '');
    setOpen(true);
  };
  const save = (): void => {
    const kopeks = value ? rubToKopeks(value) : 0;
    if (kopeks < 0) return;
    mutation.mutate(kopeks);
  };

  if (disabled) {
    return (
      <Tooltip title={disabledHint}>
        <Typography.Text type="secondary">—</Typography.Text>
      </Tooltip>
    );
  }

  return (
    <Popover
      open={open}
      onOpenChange={(next) => setOpen(next)}
      trigger="click"
      destroyTooltipOnHide
      // Body-mounted on purpose, overriding the app-wide getPopupContainer
      // (parent element): inside the table's horizontal scroll container the
      // mounting popup + autofocused input yank scrollLeft back to 0 on mobile.
      getPopupContainer={() => document.body}
      content={
        <div onClick={(e) => e.stopPropagation()} style={{ width: 200, display: 'grid', gap: 8 }}>
          <AmountInput value={value} onChange={setValue} autoFocus size="middle" />
          <Space>
            <Button type="primary" size="small" loading={mutation.isPending} onClick={save}>
              {t('common.save')}
            </Button>
            <Button size="small" onClick={() => setOpen(false)}>
              {t('common.cancel')}
            </Button>
          </Space>
        </div>
      }
    >
      <span
        role="button"
        onClick={(e) => {
          e.stopPropagation();
          startEdit();
        }}
        // Full-bleed: negative margins cancel the small-table cell padding so the
        // WHOLE cell is the click target, not just the «+» / value.
        style={{
          cursor: 'pointer',
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
          margin: -8,
          padding: 8,
          minHeight: 22,
        }}
      >
        {amount ? (
          money(amount)
        ) : (
          <Typography.Text type="secondary" style={{ fontSize: 18, lineHeight: 1 }}>
            +
          </Typography.Text>
        )}
      </span>
    </Popover>
  );
}

/**
 * «Расход» cell (admin day-grid). Shows the day's non-salary expense total and,
 * on tap, a form to create a NEW expense for THIS day+store (category + amount +
 * optional comment + «вычесть из остатка»). Unlike cash/terminal/salary a day
 * may hold several expenses, so each submit ADDS one (posted immediately — admin
 * side, no approval step). The category picker excludes the salary category
 * (that money goes through the Oylik column). */
function ExpenseCell({
  amount,
  storeId,
  date,
  categories,
}: {
  amount: number;
  storeId: string;
  date: string;
  categories: ExpenseCategory[];
}): JSX.Element {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [categoryId, setCategoryId] = useState<string | undefined>(categories[0]?.id);
  const [value, setValue] = useState('');
  const [comment, setComment] = useState('');
  const [deduct, setDeduct] = useState(false);

  const mutation = useMutation({
    mutationFn: () =>
      operationsApi.create({
        type: 'EXPENSE',
        date,
        amount: rubToKopeks(value),
        storeId,
        categoryId,
        ...(deduct ? { deductFromStoreBalance: true } : {}),
        ...(comment.trim() ? { comment: comment.trim() } : {}),
      }),
    onSuccess: () => {
      invalidateMoneyData(queryClient);
      message.success(t('quick.expenseSuccess'));
      setOpen(false);
      setValue('');
      setComment('');
    },
    onError: (error: Error) => message.error(apiErrorMessage(error) ?? t('common.requestError')),
  });

  const submit = (): void => {
    if (!categoryId) {
      message.error(t('common.categoryRequired'));
      return;
    }
    const kopeks = value ? rubToKopeks(value) : 0;
    if (kopeks <= 0) return;
    mutation.mutate();
  };

  const startEdit = (): void => {
    setValue('');
    setComment('');
    setDeduct(false);
    setCategoryId((prev) => prev ?? categories[0]?.id);
    setOpen(true);
  };

  return (
    <Popover
      open={open}
      onOpenChange={(next) => setOpen(next)}
      trigger="click"
      destroyTooltipOnHide
      // Body-mounted on purpose, overriding the app-wide getPopupContainer
      // (parent element): inside the table's horizontal scroll container the
      // mounting popup + autofocused input yank scrollLeft back to 0 on mobile.
      getPopupContainer={() => document.body}
      title={`${dayjs(date).format('DD.MM')} · ${t('opType.EXPENSE')}`}
      content={
        <div onClick={(e) => e.stopPropagation()} style={{ width: 240, display: 'grid', gap: 8 }}>
          {categories.length === 0 ? (
            <Typography.Text type="secondary">{t('settings.categoriesEmpty')}</Typography.Text>
          ) : (
            <>
              <Select
                size="middle"
                value={categoryId}
                onChange={setCategoryId}
                placeholder={t('quick.categoryPlaceholder')}
                options={categories.map((c) => ({ value: c.id, label: c.name }))}
                showSearch
                optionFilterProp="label"
              />
              <AmountInput value={value} onChange={setValue} autoFocus size="middle" />
              <Input
                placeholder={t('common.commentPlaceholder')}
                value={comment}
                onChange={(e) => setComment(e.target.value)}
              />
              <Checkbox checked={deduct} onChange={(e) => setDeduct(e.target.checked)}>
                {t('quick.deductFromStore')}
              </Checkbox>
              <Space>
                <Button type="primary" size="small" loading={mutation.isPending} onClick={submit}>
                  {t('common.save')}
                </Button>
                <Button size="small" onClick={() => setOpen(false)}>
                  {t('common.cancel')}
                </Button>
              </Space>
            </>
          )}
        </div>
      }
    >
      <span
        role="button"
        onClick={(e) => {
          e.stopPropagation();
          startEdit();
        }}
        style={{
          cursor: 'pointer',
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
          margin: -8,
          padding: 8,
          minHeight: 22,
        }}
      >
        {amount ? (
          money(amount)
        ) : (
          <Typography.Text type="secondary" style={{ fontSize: 18, lineHeight: 1 }}>
            +
          </Typography.Text>
        )}
      </span>
    </Popover>
  );
}

/** One dynamic/static money column carrying its own summary-total cell. */
interface GridCol {
  key: string;
  title: string;
  render: (row: StoreDayGridDay) => JSX.Element;
  total: JSX.Element;
  /** Second, orange line under the total: the month's PENDING sum. */
  pendingTotal?: JSX.Element;
  fixed?: 'left';
  width?: number;
  /** Accent hex («#rrggbb») for a sklad column — tints header, cells, total. */
  color?: string | null;
}

/**
 * Store «Jadval» view: one row per calendar day; issued amounts spread across
 * a column per source sklad, plus cash/terminal received, the editable Oylik
 * cell, other expenses, and the running store balance. Tap a row to see the
 * day's underlying operations.
 */
export function StoreDayGridTable({
  id,
  month,
  readOnly = false,
}: {
  id: string;
  month: string;
  /** SELLER view: cells are not editable and the day-detail drawer (which shows
   * raw operations, incl. buy amounts) is disabled. */
  readOnly?: boolean;
}): JSX.Element {
  const { t } = useTranslation();
  const sticky = useStickyTable();
  const location = useLocation();
  const [detailDate, setDetailDate] = useState<string | null>(null);

  const query = useQuery({
    queryKey: queryKeys.dayGrid('store', id, month),
    queryFn: () => reportsApi.storeDayGrid(id, month),
  });

  // Expense categories feed the «Расход» cell's create form (admin view only).
  const categoriesQuery = useQuery({
    queryKey: queryKeys.categories(false),
    queryFn: () => categoriesApi.list(false),
    enabled: !readOnly,
    staleTime: 5 * 60_000,
  });

  // Auto-scroll to today's row is disabled for now (highlight stays).

  if (query.isLoading) return <LoadingBlock />;
  if (query.isError || !query.data) return <ErrorBlock onRetry={() => void query.refetch()} />;
  const grid = query.data;
  const { totals } = grid;
  const salaryDisabled = grid.salaryCategoryId === null;
  const closing = grid.days.at(-1)?.balanceAfter ?? 0;
  // The «Расход» cell creates non-salary expenses, so the salary category is
  // excluded (that money goes through the Oylik column instead).
  const expenseCategories = (categoriesQuery.data ?? []).filter(
    (c) => c.id !== grid.salaryCategoryId,
  );
  const today = dayjs().format('YYYY-MM-DD');

  /** The «ожидает» line for one cell — null when nothing waits there, so the
   * month's mostly-empty cells mount no popover machinery at all. */
  const pendingCell = (row: StoreDayGridDay, column: PendingColumn): JSX.Element | null =>
    row.pending[column] ? (
      <PendingCellPopover
        amount={row.pending[column]}
        requests={row.pendingRequests.filter((r) => r.column === column)}
        date={row.date}
        column={column}
        categories={categoriesQuery.data ?? []}
        salaryCategoryId={grid.salaryCategoryId}
        readOnly={readOnly}
      />
    ) : null;
  /** Month total of the pending money in one column, for the summary row. */
  const pendingTotal = (column: PendingColumn): JSX.Element | undefined =>
    grid.pendingTotals[column] ? (
      <div style={{ color: UNUSUAL_NEGATIVE, fontSize: 12, whiteSpace: 'nowrap' }}>
        {t('daygrid.pendingLine', { sum: formatKopeks(grid.pendingTotals[column]) })}
      </div>
    ) : undefined;

  // Unposted накладные per sklad column ('' = «Прочее»): the cell line and the
  // summary line share one label, so a waiting document reads the same in both.
  const returnTo = jadvalReturnTo(location.pathname, month);
  const pendingShipmentsCell = (row: StoreDayGridDay, skladId: string, title: string) => (
    <PendingShipmentsCell
      docs={row.pendingShipments.filter((d) => d.skladId === skladId)}
      date={row.date}
      columnTitle={title}
      returnTo={returnTo}
    />
  );
  const pendingShipmentsTotal = (skladId: string): JSX.Element | undefined => {
    const seen = new Map<string, { msName: string }>();
    for (const row of grid.days)
      for (const d of row.pendingShipments) if (d.skladId === skladId) seen.set(d.id, d);
    if (seen.size === 0) return undefined;
    const sum = grid.pendingTotals.issuedBySklad[skladId] ?? 0;
    return (
      <div style={{ color: UNUSUAL_NEGATIVE, fontSize: 12, fontWeight: 400, whiteSpace: 'nowrap' }}>
        {pendingShipmentsLabel(t, sum, [...seen.values()])}
      </div>
    );
  };

  const cols: GridCol[] = [
    {
      key: 'date',
      title: t('common.date'),
      fixed: 'left',
      width: 62,
      render: (row) => {
        const d = dayjs(row.date);
        const weekend = d.day() === 0 || d.day() === 6;
        return (
          <span style={{ fontVariantNumeric: 'tabular-nums', color: weekend ? '#cf1322' : undefined }}>
            {d.format('DD.MM')}
          </span>
        );
      },
      total: (
        <>
          <strong>{t('daygrid.total')}</strong>
          {grid.pendingTotals.count > 0 ? (
            <div style={{ color: UNUSUAL_NEGATIVE, fontSize: 12, fontWeight: 400 }}>
              {t('daygrid.pendingCount', { n: grid.pendingTotals.count })}
            </div>
          ) : null}
        </>
      ),
    },
    ...grid.sklads.map<GridCol>((s) => ({
      key: `sklad-${s.id}`,
      title: s.name,
      render: (row) => (
        <CellStack
          main={money(row.issuedBySklad[s.id] ?? 0)}
          pending={pendingShipmentsCell(row, s.id, s.name)}
        />
      ),
      total: money(totals.issuedBySklad[s.id] ?? 0),
      pendingTotal: pendingShipmentsTotal(s.id),
      color: s.color,
    })),
    ...(grid.hasOther
      ? [
          {
            key: 'other',
            title: t('daygrid.other'),
            render: (row: StoreDayGridDay) => (
              <CellStack
                main={money(row.issuedOther)}
                pending={pendingShipmentsCell(row, '', t('daygrid.other'))}
              />
            ),
            total: money(totals.issuedOther),
            pendingTotal: pendingShipmentsTotal(''),
          },
        ]
      : []),
    {
      key: 'cash',
      title: t('daygrid.colCash'),
      render: (row) => (
        <CellStack
          main={
            readOnly ? (
              money(row.cash)
            ) : (
              <EditableMoneyCell
                amount={row.cash}
                save={(kopeks) => operationsApi.setDayPayment(id, row.date, 'CASH', kopeks)}
              />
            )
          }
          pending={pendingCell(row, 'cash')}
        />
      ),
      total: money(totals.cash),
      pendingTotal: pendingTotal('cash'),
    },
    {
      key: 'terminal',
      title: t('daygrid.colTerminal'),
      render: (row) => (
        <CellStack
          main={
            readOnly ? (
              money(row.terminal)
            ) : (
              <EditableMoneyCell
                amount={row.terminal}
                save={(kopeks) => operationsApi.setDayPayment(id, row.date, 'TERMINAL', kopeks)}
              />
            )
          }
          pending={pendingCell(row, 'terminal')}
        />
      ),
      total: money(totals.terminal),
      pendingTotal: pendingTotal('terminal'),
    },
    // Informational fee/НДС columns; hidden while the store's rate is 0 (the
    // backend also zeroes the rates for sellers, hiding them there for free).
    ...(grid.terminalFeeBps > 0
      ? [
          {
            key: 'terminalFee',
            title: t('daygrid.colTerminalFee'),
            render: (row: StoreDayGridDay) => money(row.terminalFee),
            total: money(totals.terminalFee),
          },
        ]
      : []),
    ...(grid.taxBps > 0
      ? [
          {
            key: 'tax',
            title: t('daygrid.colTax'),
            render: (row: StoreDayGridDay) => money(row.tax),
            total: money(totals.tax),
          },
        ]
      : []),
    {
      key: 'salary',
      title: t('daygrid.colSalary'),
      render: (row) => (
        <CellStack
          main={
            readOnly ? (
              money(row.salary)
            ) : (
              <EditableMoneyCell
                amount={row.salary}
                save={(kopeks) => operationsApi.setDaySalary(id, row.date, kopeks)}
                disabled={salaryDisabled}
                disabledHint={t('daygrid.salaryDisabled')}
              />
            )
          }
          pending={pendingCell(row, 'salary')}
        />
      ),
      total: money(totals.salary),
      pendingTotal: pendingTotal('salary'),
    },
    {
      key: 'expenses',
      title: t('daygrid.colExpenses'),
      render: (row) => (
        <CellStack
          main={
            readOnly ? (
              money(row.expenses)
            ) : (
              <ExpenseCell
                amount={row.expenses}
                storeId={id}
                date={row.date}
                categories={expenseCategories}
              />
            )
          }
          pending={pendingCell(row, 'expenses')}
        />
      ),
      total: money(totals.expenses),
      pendingTotal: pendingTotal('expenses'),
    },
    {
      key: 'balance',
      title: t('daygrid.colBalance'),
      render: (row) => (
        <span style={{ color: balanceColor('store', row.balanceAfter), whiteSpace: 'nowrap' }}>
          {formatKopeks(row.balanceAfter)}
        </span>
      ),
      total: (
        <span style={{ color: balanceColor('store', closing), whiteSpace: 'nowrap' }}>
          {formatKopeks(closing)}
        </span>
      ),
    },
  ];

  const columns: ColumnsType<StoreDayGridDay> = cols.map((c) => {
    const editable = ['salary', 'cash', 'terminal', 'expenses'].includes(c.key);
    const headerTint = hexTint(c.color, 0.22);
    const cellTint = hexTint(c.color, 0.1);
    return {
      title: c.title,
      key: c.key,
      align: c.key === 'date' ? 'left' : 'right',
      fixed: c.fixed,
      width: c.width,
      onHeaderCell: headerTint ? () => ({ style: { background: headerTint } }) : undefined,
      // Editable cells (salary + cash/terminal) swallow the row click so tapping
      // to edit doesn't also open the day drawer; sklad columns carry their tint.
      onCell:
        editable || cellTint
          ? () => ({
              ...(editable ? { onClick: (e: MouseEvent) => e.stopPropagation() } : {}),
              ...(cellTint ? { style: { background: cellTint } } : {}),
            })
          : undefined,
      render: (_v: unknown, row: StoreDayGridDay) => c.render(row),
    };
  });

  return (
    <>
      <Table<StoreDayGridDay>
        dataSource={grid.days}
        columns={columns}
        rowKey="date"
        size="small"
        bordered
        pagination={false}
        sticky={sticky}
        scroll={{ x: 'max-content' }}
        rowClassName={(row) => (row.date === today ? 'jadval-today' : '')}
        onRow={(row) => ({
          ...(row.date === today ? { id: TODAY_ROW_ID } : {}),
          ...(readOnly
            ? {}
            : { onClick: () => setDetailDate(row.date), style: { cursor: 'pointer' } }),
        })}
        summary={() => (
          <Table.Summary>
            <Table.Summary.Row style={{ fontWeight: 600, background: 'rgba(0,0,0,0.02)' }}>
              {cols.map((c, i) => {
                // Summary.Cell forwards no `style`, so a full-bleed inner span
                // (negative margins cancel the small-table cell padding) carries
                // the sklad tint across the whole total cell.
                const tint = hexTint(c.color, 0.1);
                return (
                  <Table.Summary.Cell
                    key={c.key}
                    index={i}
                    align={c.key === 'date' ? 'left' : 'right'}
                  >
                    {tint ? (
                      <span style={{ display: 'block', margin: -8, padding: 8, background: tint }}>
                        {c.total}
                      </span>
                    ) : (
                      c.total
                    )}
                    {c.pendingTotal}
                  </Table.Summary.Cell>
                );
              })}
            </Table.Summary.Row>
          </Table.Summary>
        )}
      />
      <DayDetailDrawer kind="store" id={id} date={detailDate} onClose={() => setDetailDate(null)} />
    </>
  );
}
